import React, { useMemo } from "react";
import "./Disclaimer.css";
import { useLocalContext } from "../../context/LocalContext";

const Disclaimer = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+91 80000 00000",
      phonecall: webinfo.phonecall || "+918000000000",
      address: webinfo.address || "Remote-first • Serving clients across cities",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="privacy-policy">
      <div className="pp-wrap">
        {/* Header */}
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Important notice</p>
          <div className="pp-header-main">
            <h1>Website Disclaimer</h1>
          </div>
          <p className="pp-meta">
            <span>Issued by: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
          </p>
        </header>

        {/* Content */}
        <div className="privacy-policy-content">
          <section>
            <h2>1. General information only</h2>
            <p>
              The content on this website is shared in good faith to explain what we do and how
              digital marketing, websites, ads and SEO can help your business. We try to keep
              everything accurate and simple, but information can change and may not always be
              complete or up to date.
            </p>
            <p>
              Nothing on this website is a promise, guarantee, or final blueprint for your exact
              situation. You should always treat it as general information, not as a personal
              recommendation.
            </p>
          </section>

          <section>
            <h2>2. Not personal or professional advice</h2>
            <p>
              The ideas, tips and examples we share (for websites, social media, ads, SEO, emails,
              funnels, etc.) are based on our experience, but they are not personalised advice for
              your business.
            </p>
            <p>
              Before you make important decisions about your business, money, or legal matters, you
              should speak to a qualified professional (for example, an accountant, lawyer or
              specialist advisor) who understands your exact situation. Any action you take based on
              information from this website is at your own risk.
            </p>
          </section>

          <section>
            <h2>3. No guaranteed marketing results</h2>
            <p>
              Marketing and growth results always depend on many different factors — such as your
              offer, market, competition, pricing, budget, content, timing and internal follow-up.
            </p>
            <p>
              Any examples of leads, traffic, enquiries, rankings, sales or campaign performance
              that we share (including case studies, testimonials or screenshots) are there to show
              what has worked in specific situations. They do <strong>not</strong> guarantee that
              you will get the same outcome, even if you do similar work.
            </p>
          </section>

          <section>
            <h2>4. Use of tools, AI &amp; automation</h2>
            <p>
              In our work and on this website, {brand.name} may use trusted tools, including AI and
              automation, to help with research, ideas, drafts, testing, or reporting.
            </p>
            <p>
              We aim to review and refine important content before sharing it, but AI and other
              tools can still be imperfect or out of date. You should not rely on any information
              here as legal, financial or tax advice, and you remain responsible for checking that
              anything you use is suitable for your own business.
            </p>
          </section>

          <section>
            <h2>5. External links &amp; third-party content</h2>
            <p>
              Our website may link to other websites, tools, articles, videos or resources. These
              are provided only for convenience and additional information.
            </p>
            <p>
              {brand.name} does not control and is not responsible for the content, policies,
              security, or availability of any external websites. Including a link does not mean we
              endorse that site or agree with everything on it. You visit or use external websites
              at your own risk.
            </p>
          </section>

          <section>
            <h2>6. Platforms, ad accounts &amp; policy decisions</h2>
            <p>
              We often work with platforms such as Google, Meta (Facebook/Instagram), LinkedIn,
              email tools, CRMs, website builders and hosting providers. Each of these platforms has
              its own rules, review process and policies, which can change over time.
            </p>
            <p>
              {brand.name} cannot control or guarantee how these platforms will treat your accounts,
              ads or content. We are not responsible for policy changes, disapprovals, restricted or
              banned accounts, or other actions taken by third-party platforms, even if we have
              helped set up or manage your campaigns or website.
            </p>
          </section>

          <section>
            <h2>7. Technical issues, errors &amp; uptime</h2>
            <p>
              We want this website to be available and easy to use, but like any online service it
              may sometimes be slow, down, or show errors because of hosting issues, maintenance,
              internet problems or other technical reasons.
            </p>
            <p>
              {brand.name} does not guarantee that the website will always be online, secure or
              error-free, and will not be responsible for any loss, damage or inconvenience caused
              by downtime or technical problems.
            </p>
          </section>

          <section>
            <h2>8. Limitation of liability</h2>
            <p>
              To the maximum extent allowed by law, {brand.name} and our team are not liable for any
              direct or indirect loss or damage that comes from:
            </p>
            <ul>
              <li>using this website or relying on its content, or</li>
              <li>being unable to use this website or any of its features.</li>
            </ul>
            <p>
              This includes (but is not limited to) lost profits, lost business, lost data, loss of
              reputation, or any other types of loss, even if you have informed us that such loss
              might happen.
            </p>
          </section>

          <section>
            <h2>9. Changes to this Disclaimer</h2>
            <p>
              We may update this Disclaimer from time to time if our services, tools or the law
              changes. When we do, we will adjust the “Last updated” date at the top of this page.
            </p>
            <p>
              If you continue to use this website after we publish an updated version, it will be
              treated as your acceptance of the new wording.
            </p>
          </section>

          <section>
            <h2>10. How to contact us</h2>
            <p>
              If you have questions about this Disclaimer or want to know how it applies to your
              use of our website or services, you can contact us here:
            </p>

            <ul className="pp-contact-list">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
              </li>
              <li>
                <strong>Address:</strong> {brand.address}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Disclaimer;
